/**
 * Página de Monitoramento da Fila de Webhooks PGMQ
 * 
 * Dashboard completo para visualização de estatísticas das filas PGMQ
 * de webhooks (routing e deals), incluindo métricas, gráficos de volume,
 * alertas de saúde e tabela de itens.
 */

import { useState } from 'react';
import { RefreshCw, Webhook, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQueryClient } from '@tanstack/react-query';
import { WebhookQueueStatusCards } from '@/components/dashboard/WebhookQueueStatusCards';
import { WebhookQueueVolumeChart } from '@/components/dashboard/WebhookQueueVolumeChart';
import { WebhookQueueHealthAlerts } from '@/components/dashboard/WebhookQueueHealthAlerts';
import { WebhookQueueItemsTable } from '@/components/dashboard/WebhookQueueItemsTable';
import { WebhookQueueSlowestItems } from '@/components/dashboard/WebhookQueueSlowestItems';
import { WebhookQueueType } from '@/hooks/useWebhookQueueMonitor';

export default function WebhookQueueMonitor() {
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeQueue, setActiveQueue] = useState<WebhookQueueType>('routing');

  // Função para atualizar todos os dados
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
      // Invalida todas as queries relacionadas às filas de webhooks
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['webhook-queue-stats'] }),
        queryClient.invalidateQueries({ queryKey: ['webhook-queue-items'] }),
        queryClient.invalidateQueries({ queryKey: ['webhook-queue-volume'] }),
        queryClient.invalidateQueries({ queryKey: ['webhook-queue-alerts'] }),
        queryClient.invalidateQueries({ queryKey: ['webhook-queue-slowest'] }),
      ]);
    
    setLastUpdate(new Date());
    setIsRefreshing(false);
  };

  // Formatar tempo desde última atualização
  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `há ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `há ${minutes}min`;
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Webhook className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Fila de Webhooks</h1>
            <p className="text-muted-foreground">
              Monitoramento das filas PGMQ de Routing e Deals
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Atualizado {getTimeSinceUpdate()}</span>
          </div>
          <Button 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs para selecionar fila */}
      <Tabs 
        value={activeQueue} 
        onValueChange={(value) => setActiveQueue(value as WebhookQueueType)}
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="routing" className="gap-2">
            🔀 Routing
          </TabsTrigger>
          <TabsTrigger value="deals" className="gap-2">
            💼 Deals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="routing" className="space-y-6">
          {/* Cards de Status */}
          <WebhookQueueStatusCards queue="routing" />

          {/* Alertas de Saúde */}
          <WebhookQueueHealthAlerts queue="routing" />

          {/* Gráficos */}
          <div className="grid gap-6 md:grid-cols-1">
            <WebhookQueueVolumeChart queue="routing" />
          </div>

          {/* KPI: Top 20 Itens Mais Lentos */}
          <WebhookQueueSlowestItems queue="routing" />

          {/* Tabela de Itens */}
          <WebhookQueueItemsTable queue="routing" />
        </TabsContent>

        <TabsContent value="deals" className="space-y-6">
          {/* Cards de Status */}
          <WebhookQueueStatusCards queue="deals" />

          {/* Alertas de Saúde */}
          <WebhookQueueHealthAlerts queue="deals" />

          {/* Gráficos */}
          <div className="grid gap-6 md:grid-cols-1">
            <WebhookQueueVolumeChart queue="deals" />
          </div>

          {/* KPI: Top 20 Itens Mais Lentos */}
          <WebhookQueueSlowestItems queue="deals" />

          {/* Tabela de Itens */}
          <WebhookQueueItemsTable queue="deals" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
